import { create } from 'zustand'
import {
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  type NodeChange,
  type EdgeChange,
  type Connection,
  type Node,
  type Edge,
} from '@xyflow/react'
import type { Workflow, WorkflowCreateInput } from '@/schemas/workflow'
import type { FlowNode, FlowEdge, NodeType } from '@/schemas/workflow-node'
import {
  defaultConfigByType,
  isWrappedNodeConfig,
  migrateNodeConfigToInheritanceBlob,
} from '@/schemas/workflow-node'
import type { LLMProviderValue } from '@/schemas/llm-api-key'
import { workflowService } from '@/services/workflow'
import { persistGraph } from '@/stores/workflow-store-persistence'
import { normalizeGraphToReactFlow } from '@/components/workflows/flow/normalize'

// --- Phase 4 plan 04-04: default-LLM config shape ---
// Mirrors the JSONB blob stored under `Workflow.settings.default_llm` (D-05).
export interface DefaultLlmConfig {
  provider: LLMProviderValue | null
  model: string | null
  temperature: number | null
  max_tokens: number | null
}

/**
 * Detect if adding edge from→to would create a cycle using DFS.
 * Returns true if a cycle would be created.
 */
export function wouldCreateCycle(
  edges: Edge[],
  from: string,
  to: string
): boolean {
  // If the new edge goes from→to, check if there's already a path from to→from
  const adjacency = new Map<string, string[]>()
  for (const edge of edges) {
    const neighbors = adjacency.get(edge.source) ?? []
    neighbors.push(edge.target)
    adjacency.set(edge.source, neighbors)
  }

  // Add the proposed edge
  const fromNeighbors = adjacency.get(from) ?? []
  fromNeighbors.push(to)
  adjacency.set(from, fromNeighbors)

  // DFS from 'to' to see if we can reach 'from'
  const visited = new Set<string>()
  const stack = [to]

  while (stack.length > 0) {
    const current = stack.pop()!
    if (current === from) return true
    if (visited.has(current)) continue
    visited.add(current)

    const neighbors = adjacency.get(current) ?? []
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        stack.push(neighbor)
      }
    }
  }

  return false
}

interface WorkflowState {
  // --- Phase 1 state ---
  workflows: Workflow[]
  total: number
  page: number
  pageSize: number
  totalPages: number
  loading: boolean
  error: string | null
  currentWorkflow: Workflow | null

  // --- Phase 2 flow state ---
  flowNodes: Node<FlowNode>[]
  flowEdges: Edge[]
  selectedNodeId: string | null
  selectedEdgeId: string | null
  flowViewport: { x: number; y: number; zoom: number }

  // --- Phase 4 default-LLM state ---
  // Dirty flag flips when setDefaultLlm mutates without persisting to disk.
  // Default LLM values themselves live on currentWorkflow.settings.default_llm
  // (D-05) so there's a single source of truth.
  defaultLlmDirty: boolean

  // --- Phase 1 actions ---
  fetchWorkflows: (projectId: string, page?: number) => Promise<void>
  fetchWorkflow: (projectId: string, workflowId: string) => Promise<void>
  createWorkflow: (
    projectId: string,
    data: WorkflowCreateInput
  ) => Promise<Workflow>
  updateWorkflow: (
    projectId: string,
    workflowId: string,
    data: Record<string, unknown>
  ) => Promise<void>
  deleteWorkflow: (projectId: string, workflowId: string) => Promise<void>
  reset: () => void

  // --- Phase 2 flow actions ---
  setFlowFromWorkflow: (workflow: Workflow) => void
  onNodesChange: (changes: NodeChange[]) => void
  onEdgesChange: (changes: EdgeChange[]) => void
  onConnect: (connection: Connection) => void
  addNode: (type: NodeType, position: { x: number; y: number }) => void
  removeNode: (nodeId: string) => void
  updateNodeConfig: (nodeId: string, data: Partial<FlowNode>) => void
  selectNode: (nodeId: string | null) => void
  selectEdge: (edgeId: string | null) => void
  updateEdgeCondition: (
    edgeId: string,
    condition: FlowEdge['condition'],
    branchLabel: string | null
  ) => void
  setFlowViewport: (viewport: { x: number; y: number; zoom: number }) => void

  // --- Phase 4 default-LLM actions ---
  setDefaultLlm: (patch: Partial<DefaultLlmConfig>) => void
  applyDefaultToAllLlmNodes: () => void
}

const initialState = {
  workflows: [] as Workflow[],
  total: 0,
  page: 1,
  pageSize: 20,
  totalPages: 0,
  loading: false,
  error: null as string | null,
  currentWorkflow: null as Workflow | null,

  // Phase 2 flow state
  flowNodes: [] as Node<FlowNode>[],
  flowEdges: [] as Edge[],
  selectedNodeId: null as string | null,
  selectedEdgeId: null as string | null,
  flowViewport: { x: 0, y: 0, zoom: 1 },

  // Phase 4 default-LLM state
  defaultLlmDirty: false,
}

export const useWorkflowStore = create<WorkflowState>((set, get) => ({
  ...initialState,

  // --- Phase 1 actions (unchanged) ---

  fetchWorkflows: async (projectId, page = 1) => {
    set({ loading: true, error: null })
    try {
      const data = await workflowService.list(projectId, {
        page,
        page_size: get().pageSize,
      })
      set({
        workflows: data.workflows,
        total: data.total,
        page: data.page,
        totalPages: data.total_pages,
        loading: false,
      })
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to fetch workflows'
      set({ error: message, loading: false })
    }
  },

  fetchWorkflow: async (projectId, workflowId) => {
    set({ loading: true, error: null })
    try {
      const data = await workflowService.get(projectId, workflowId)
      set({ currentWorkflow: data, loading: false })
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to fetch workflow'
      set({ error: message, loading: false })
    }
  },

  createWorkflow: async (projectId, input) => {
    try {
      const data = await workflowService.create(projectId, input)
      set((state) => ({
        workflows: [data, ...state.workflows],
        error: null,
      }))
      return data
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to create workflow'
      set({ error: message })
      throw e
    }
  },

  updateWorkflow: async (projectId, workflowId, input) => {
    try {
      const data = await workflowService.update(projectId, workflowId, input)
      set((state) => ({
        currentWorkflow: data,
        workflows: state.workflows.map((w) =>
          w.id === workflowId ? data : w
        ),
        error: null,
      }))
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to update workflow'
      set({ error: message })
      throw e
    }
  },

  deleteWorkflow: async (projectId, workflowId) => {
    try {
      await workflowService.delete(projectId, workflowId)
      set((state) => ({
        workflows: state.workflows.filter((w) => w.id !== workflowId),
        error: null,
      }))
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to delete workflow'
      set({ error: message })
      throw e
    }
  },

  reset: () => set(initialState),

  // --- Phase 2 flow actions ---

  setFlowFromWorkflow: (workflow: Workflow) => {
    // Delegate to the pure `normalizeGraphToReactFlow` helper so the same
    // normalization logic is reused by the read-only diff canvas (Phase 03).
    const { nodes: flowNodes, edges: flowEdges } = normalizeGraphToReactFlow(
      workflow.nodes as unknown as ReadonlyArray<Record<string, unknown>>,
      workflow.edges as unknown as ReadonlyArray<Record<string, unknown>>
    )
    set({ flowNodes, flowEdges })
  },

  onNodesChange: (changes: NodeChange[]) => {
    const state = get()
    const updatedNodes = applyNodeChanges(changes, state.flowNodes)

    // On remove changes, also filter out edges connected to removed nodes
    const removedNodeIds = changes
      .filter(
        (c): c is NodeChange & { type: 'remove'; id: string } =>
          c.type === 'remove'
      )
      .map((c) => c.id)

    let updatedEdges = state.flowEdges
    if (removedNodeIds.length > 0) {
      updatedEdges = state.flowEdges.filter(
        (edge) =>
          !removedNodeIds.includes(edge.source) &&
          !removedNodeIds.includes(edge.target)
      )
    }

    set({
      flowNodes: updatedNodes as Node<FlowNode>[],
      flowEdges: updatedEdges,
    })
    persistGraph(get)
  },

  onEdgesChange: (changes: EdgeChange[]) => {
    set({
      flowEdges: applyEdgeChanges(changes, get().flowEdges),
    })
  },

  onConnect: (connection: Connection) => {
    const state = get()
    if (!connection.source || !connection.target) return

    // Check for cycles before adding edge
    if (
      wouldCreateCycle(state.flowEdges, connection.source, connection.target)
    ) {
      // Do not add edge — consumer should show toast
      return
    }

    set({
      flowEdges: addEdge(connection, state.flowEdges),
    })
    persistGraph(get)
  },

  addNode: (type: NodeType, position: { x: number; y: number }) => {
    const id = crypto.randomUUID()
    const newNode: Node<FlowNode> = {
      id,
      type,
      position,
      data: {
        type,
        id,
        label: '',
        position,
        config: defaultConfigByType[type],
        input_schema: [],
        output_schema: [],
        variable_mappings: [],
        error_handling: {
          retry_count: 0,
          retry_backoff: 'linear',
          fallback_node_id: null,
          on_error: 'fail_workflow',
          default_output: null,
        },
      } as FlowNode,
    }

    set((state) => ({
      flowNodes: [...state.flowNodes, newNode],
    }))
    persistGraph(get)
  },

  removeNode: (nodeId: string) => {
    set((state) => ({
      flowNodes: state.flowNodes.filter((n) => n.id !== nodeId),
      flowEdges: state.flowEdges.filter(
        (e) => e.source !== nodeId && e.target !== nodeId
      ),
      selectedNodeId:
        state.selectedNodeId === nodeId ? null : state.selectedNodeId,
    }))
    persistGraph(get)
  },

  updateNodeConfig: (nodeId: string, data: Partial<FlowNode>) => {
    set((state) => ({
      flowNodes: state.flowNodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...data } as FlowNode }
          : node
      ),
    }))
    persistGraph(get)
  },

  selectNode: (nodeId: string | null) => {
    set({ selectedNodeId: nodeId, selectedEdgeId: null })
  },

  selectEdge: (edgeId: string | null) => {
    set({ selectedEdgeId: edgeId, selectedNodeId: null })
  },

  updateEdgeCondition: (
    edgeId: string,
    condition: FlowEdge['condition'],
    branchLabel: string | null
  ) => {
    set((state) => ({
      flowEdges: state.flowEdges.map((edge) =>
        edge.id === edgeId
          ? {
              ...edge,
              data: {
                ...(edge.data ?? {}),
                condition,
                branch_label: branchLabel,
              },
            }
          : edge
      ),
    }))
    persistGraph(get)
  },

  setFlowViewport: (viewport: { x: number; y: number; zoom: number }) => {
    set({ flowViewport: viewport })
  },

  // --- Phase 4 default-LLM actions ---

  setDefaultLlm: (patch: Partial<DefaultLlmConfig>) => {
    set((state) => {
      if (!state.currentWorkflow) return {}
      const currentSettings =
        (state.currentWorkflow.settings as Record<string, unknown> | null) ?? {}
      const currentDefault =
        (currentSettings.default_llm as Partial<DefaultLlmConfig> | undefined) ??
        {}
      const nextDefault = { ...currentDefault, ...patch }
      return {
        currentWorkflow: {
          ...state.currentWorkflow,
          settings: {
            ...currentSettings,
            default_llm: nextDefault,
          },
        } as Workflow,
        defaultLlmDirty: true,
      }
    })
  },

  applyDefaultToAllLlmNodes: () => {
    set((state) => {
      const flowNodes = state.flowNodes.map((node) => {
        if (node.type !== 'llm') return node
        const data = node.data as unknown as {
          config?: Record<string, unknown>
        } & Record<string, unknown>
        const rawConfig = (data.config as Record<string, unknown>) ?? {}
        // If Phase 2 still produces flat shape, migrate first so we have a
        // wrapped blob to mutate (RESEARCH P-08 idempotent).
        const wrapped = isWrappedNodeConfig(rawConfig)
          ? rawConfig
          : migrateNodeConfigToInheritanceBlob(rawConfig)
        const nextConfig = {
          ...wrapped,
          model: { inherited: true, value: null },
          temperature: { inherited: true, value: null },
          max_tokens: { inherited: true, value: null },
        }
        return {
          ...node,
          data: {
            ...data,
            config: nextConfig,
          } as unknown as FlowNode,
        }
      })
      return { flowNodes }
    })
  },
}))
